import React, { useState } from "react";
import "./Offerings.scss";
import { OFFERINGS, Offering } from "./Constant";
import { useScrollReveal } from "../../../motion/useScrollReveal";

const OfferingDetail: React.FC<{ offering: Offering }> = ({ offering }) => (
  <div className="offerings-panel__text">
    <div className="offerings-panel__header">
      <img className="offerings-panel__icon" src={offering.icon} alt={offering.title} />
      <h3 className="offerings-panel__title">{offering.title}</h3>
    </div>
    <p className="offerings-panel__desc">{offering.description}</p>
    <ul className="offerings-panel__list">
      {offering.bullets.map((bullet, i) => (
        <li key={i}>
          {bullet.label ? (
            <>
              <span className="offerings-panel__bullet-label">{bullet.label}:</span>{" "}
              {bullet.text}
            </>
          ) : (
            bullet.text
          )}
        </li>
      ))}
    </ul>
  </div>
);

const Offerings: React.FC = () => {
  // First offering is active by default; hover/focus/tap activates another
  // and it stays expanded until a different one is chosen.
  const [activeId, setActiveId] = useState<number>(OFFERINGS[0].id);
  const active =
    OFFERINGS.find((offering) => offering.id === activeId) ?? OFFERINGS[0];

  // Scrolling is opt-in per offering via the `scrollable` data flag, not
  // auto-measured. Only content-heavy panels (e.g. "Game Art & Creative") set
  // it; every other panel is designed to fit within the fixed panel height and
  // never shows a scrollbar.
  const isScrollable = active.scrollable === true;

  // Heading, tabs and panel rise + fade in on scroll, matching the hero's feel.
  const revealRef = useScrollReveal<HTMLDivElement>({
    selector: ".offerings-section__heads, .offerings-tabs, .offerings-panel, .offerings-accordion",
    stagger: 0.12,
  });

  return (
    <section id="offerings" className="offerings-section" aria-label="Our offerings">
      <div className="offerings-section__inner" ref={revealRef}>
        <header className="offerings-section__heads">
          <h2 className="offerings-section__title">Our Services</h2>
        </header>

        {/* Desktop / tablet: vertical tabs + shared detail panel */}
        <div className="offerings-section__body">
          <ul
            className="offerings-tabs"
            role="tablist"
            aria-orientation="vertical"
          >
            {OFFERINGS.map((offering) => (
              <li key={offering.id} role="presentation">
                <button
                  type="button"
                  role="tab"
                  id={`offering-tab-${offering.id}`}
                  aria-selected={offering.id === activeId}
                  className={`offerings-tab ${
                    offering.id === activeId ? "is-active" : ""
                  }`}
                  onMouseEnter={() => setActiveId(offering.id)}
                  onFocus={() => setActiveId(offering.id)}
                  onClick={() => setActiveId(offering.id)}
                >
                  <span className="offerings-tab__label">
                    {offering.tabLines
                      ? offering.tabLines.map((line, i) => (
                          <span key={i} style={{ display: "block" }}>{line}</span>
                        ))
                      : offering.title}
                  </span>
                  <span className="offerings-tab__arrow" aria-hidden="true">
                    →
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <div
            className={`offerings-panel ${
              isScrollable ? "offerings-panel--scrollable" : ""
            }`}
            role="tabpanel"
            aria-labelledby={`offering-tab-${active.id}`}
            style={{ backgroundImage: `url(${active.bg})` }}
          >
            {/* key remount re-triggers the entrance animation on change */}
            <OfferingDetail key={active.id} offering={active} />
          </div>
        </div>

        {/* Mobile: accordion (tap to expand, first open by default) */}
        <div className="offerings-accordion">
          {OFFERINGS.map((offering) => {
            const isOpen = offering.id === activeId;
            return (
              <div className="offerings-accordion__item" key={offering.id}>
                <button
                  type="button"
                  className={`offerings-accordion__header ${
                    isOpen ? "is-active" : ""
                  }`}
                  aria-expanded={isOpen}
                  onClick={() => setActiveId(offering.id)}
                >
                  <span className="offerings-accordion__label">{offering.title}</span>
                  <span className="offerings-accordion__icon" aria-hidden="true">
                    {isOpen ? "−" : "+"}
                  </span>
                </button>
                {isOpen && (
                  <div
                    className="offerings-accordion__panel"
                    style={{ backgroundImage: `url(${offering.bg})` }}
                  >
                    <OfferingDetail offering={offering} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Offerings;
